import React from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';

import {
  QuestionBody,
  QuestionChoiceContainer,
  QuestionWrapper,
  RemoveSelectedIcon,
} from './styles';
import styles from './styles.module.css';
import messages from './Messages';
import TitleInput from './TitleInput';
import ActionLink from 'components/ActionLink';
import DescriptionInput, {
  getDescriptionInputStatus,
} from './DescriptionInput';
import i18n from '../../i18n';

class GridQuestion extends React.Component {
  divRef = React.createRef();
  state = {
    windowWidth: window.innerWidth,
  };

  onShowDescription = (part) => {
    this.setState({
      [part]: !this.state[part],
    });
  };

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    return (
      nextProps.answer !== this.props.answer ||
      nextProps.question !== this.props.question ||
      nextState !== this.state ||
      nextProps.isRequired !== this.props.isRequired
    );
  }

  handleChange = (value, subQuestionValue, name) => {
    const { answer, question } = this.props;
    let newAnswer = answer
      ? cloneDeep(answer)
      : { questionId: question.id, subAnswers: [{ subQuestionValue }] };
    if (this.props.answer) {
      let subAnswer = newAnswer.subAnswers.find(
        (ans) => ans.subQuestionValue === subQuestionValue
      );
      subAnswer = { ...subAnswer, subQuestionValue, [name]: value };
      newAnswer.subAnswers = newAnswer.subAnswers.filter(
        (ans) => ans.subQuestionValue !== subQuestionValue
      );
      newAnswer.subAnswers.push(subAnswer);
    } else {
      newAnswer = {
        questionId: this.props.question.id,
        subAnswers: [{ subQuestionValue, [name]: value }],
      };
    }
    this.props.onChange(newAnswer, this.props.question.id);
  };

  handleShowDescription = (partValue) => {
    this.onShowDescription(partValue);
    this.handleChange(null, partValue, 'description');
  };

  handleRemoveSelectedPart = (e) => {
    const partValue = e.currentTarget.dataset.partValue;
    const { answer, question, onChange } = this.props;
    const newAnswer = cloneDeep(answer);
    newAnswer.subAnswers = newAnswer.subAnswers.filter(
      (part) => part.subQuestionValue !== partValue
    );
    onChange(newAnswer, question.id);
  };

  // FOR GAP                    // FOR EACH ITEM SIZE
  // (item.length - 1 ) * 16 + ( each item length + 56 )

  render() {
    const {
      question,
      answer,
      isRequired,
      disabled,
      tags,
      randomizeOrder,
      survey,
      answers,
      questions,
    } = this.props;

    const descriptionStatus = survey
      ? getDescriptionInputStatus(survey, question)
      : true;

    const isMobileView = this.state.windowWidth < 600;
    const isTabletView = this.state.windowWidth < 800 && !isMobileView;

    let parts = question.parts.map((it, index) => ({ ...it, index }));
    if (randomizeOrder)
      parts = parts.sort(
        (a, b) =>
          randomizeOrder[(a.index * 31 + question.id) % 100] -
          randomizeOrder[(b.index * 31 + question.id) % 100]
      );

    // (item.length - 1 ) * 16 + ( each item length + 56 )
    const colNumber = isMobileView ? 1 : isTabletView ? 2 : 3;

    const isInlineDisplayMode =
      (parts.length - 1) * 16 +
        parts.reduce((acc, part) => {
          acc = acc + part.length + 56;
          return acc;
        }, 0) <
      this.state.windowWidth - 50;

    return (
      <QuestionWrapper disabled={disabled}>
        <TitleInput
          fields={question.fields}
          hint={question.hint}
          required={question.answerRequired}
          title={question.title}
          tags={question.tags}
          allTags={tags}
          indexNumber={question.indexOrder}
          survey={survey}
          answers={answers}
          questions={questions}
        />
        <QuestionBody>
          {parts.map((part) => {
            const partAnswer = answer
              ? answer.subAnswers.find(
                  (ans) => ans.subQuestionValue === part.value
                )
              : undefined;
            const partValue = part.value;

            return (
              <div key={partValue} className="py-3">
                <h6
                  className="form-p py-3"
                  style={{
                    fontSize: '1rem',
                    fontWeight: 'normal',
                    lineHeight: '1.5rem',
                  }}
                >
                  {part.text}
                </h6>
                <QuestionChoiceContainer
                  colNumber={colNumber}
                  isInline={isMobileView && isInlineDisplayMode}
                >
                  {question.choiceList.map((choice) => {
                    const choiceValue = choice.value;
                    const isChecked =
                      (partAnswer && partAnswer.value === choiceValue) || '';
                    return (
                      <div key={choiceValue}>
                        <input
                          type="radio"
                          className="with-gap"
                          style={{
                            position: 'initial',
                            width: '1px',
                            height: '1px',
                          }}
                          value={choiceValue}
                          name={partValue}
                          required={isRequired}
                          onChange={(e) => {
                            this.handleChange(
                              e.target.value,
                              partValue,
                              'value'
                            );
                          }}
                          id={`${partValue}-${choiceValue}`}
                          checked={isChecked}
                        />
                        <label
                          className={styles.checkboxLabel}
                          htmlFor={`${partValue}-${choiceValue}`}
                        >
                          {choice.text}
                        </label>
                        {isChecked && (
                          <RemoveSelectedIcon
                            data-part-value={partValue}
                            onClick={this.handleRemoveSelectedPart}
                            type="x"
                          />
                        )}
                      </div>
                    );
                  })}
                </QuestionChoiceContainer>
                <p className={styles.space} />
                {descriptionStatus && (
                  <DescriptionInput
                    question={question}
                    survey={survey}
                    open={
                      (partAnswer && partAnswer.description) ||
                      this.state[partValue]
                    }
                    handler={() => this.handleShowDescription(partValue)}
                    id={partValue}
                    text={partAnswer?.description || ''}
                    onChangeInput={(value, name) =>
                      this.handleChange(value, partValue, name)
                    }
                    disabled={disabled}
                  />
                )}
              </div>
            );
          })}
        </QuestionBody>
      </QuestionWrapper>
    );
  }
}

GridQuestion.propTypes = {
  question: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  answer: PropTypes.object,
  disabled: PropTypes.bool,
  tags: PropTypes.object,
  randomizeOrder: PropTypes.array,
};

export default GridQuestion;
